import type { SupportedLocale } from '@erp/shared';

export const operationalSetupTranslations = {
  de: {
    operations: {
      title: 'Lager- und Lieferabläufe einrichten',
      description:
        'Optionale Funktionen, verlässliche Lagerzuordnung und geprüfte Rechnungsformate.',
      foundations: 'Vorhandene Grundlagen',
      baseline:
        'Artikel, Gebindeumrechnung, Chargen, Lagerjournal, Belegketten und Zahlungszuordnungen werden gemeinsam verwendet.',
      owner: 'Zuständig für Einrichtung',
      activation: 'Aktivierung im Feature-Katalog',
      check: 'Aktivierung prüfen',
      history: 'Bei Deaktivierung bleibt die Historie erhalten. Neue Vorgänge sind gesperrt.',
      defaults: 'Standardlager und Prüfplatz ergänzen',
      defaultsHint:
        'Ergänzt bei Bedarf Lagerstammdaten und den Platz QUARANTINE. Erzeugt keine Lagerbewegung und ordnet keine Altbestände zu.',
      warehouses: 'Aktive Lager',
      locations: 'Gepflegte Lagerplätze',
      unassigned: 'Artikel mit noch nicht zugeordnetem Bestand',
      unresolved: 'Ungeklärte Differenzen zwischen Artikel und Chargen',
      returnSetup:
        'Kundenbetreuung pflegt Gründe; Lagerleitung prüft Prüfplatz und Freigaberecht. Buchhaltung nutzt Gutschriften und nachgewiesene Erstattungen. Kein externer Dienst erforderlich.',
      pickingSetup:
        'Lagerleitung prüft Artikel- und Gebindecodes, Faktoren, Chargen und Liefertage. Tastaturscanner und manuelle Eingabe benötigen keinen Anbieter. Kamerascanning hängt vom Browser ab.',
      inventorySetup:
        'Lagerleitung ordnet historische unbekannte Mengen ausdrücklich zu und berechtigt Zähler und Abschlussprüfer. Während einer Inventur sind die betroffenen Artikel für Bewegungen gesperrt. Kein externer Dienst erforderlich.',
      tourSetup:
        'Disposition weist Fahrer mit Tourenrecht zu und prüft gespeicherte Adressen und Koordinaten. Der Betreiber richtet Straßenrouting nur bei Bedarf ein. GPS ist standardmäßig aus.',
      containerSetup:
        'Verwaltung markiert nur tatsächliche Mehrwegarten und legt Rückgabezustände fest. Buchhaltung verknüpft optional den Pfandartikel. Bestätigte Versandgebinde sind erforderlich; PDF-Schätzungen buchen keine Salden.',
      einvoiceSetup:
        'Buchhaltung vervollständigt Firmen-, Empfänger-, Steuer- und Zahlungsdaten. Der Betreiber installiert die festgelegten unabhängigen Prüfer. Ein Dateiimport erzeugt einen Entwurf zur vorhandenen Rechnungsprüfung.',
      returnCheck:
        'Mit Testlieferung Teilretoure erfassen, Sperrbestand prüfen und erst nach Prüfung freigeben.',
      pickingCheck: 'Testkarton scannen, Basismenge prüfen und Lieferscheinentwurf öffnen.',
      inventoryCheck:
        'Blindzählung mit expliziter Null testen, Differenz prüfen und berechtigt abschließen.',
      tourCheck:
        'Testlieferung zuordnen, Warenausgang buchen und als zugewiesener Fahrer Teilzustellung bestätigen.',
      containerCheck:
        'Zehn bestätigte Paletten ausgeben und sechs zurücknehmen: Saldo vier; Pfand separat prüfen.',
      einvoiceCheck:
        'Testrechnung festschreiben, extern validieren und Prüfbericht öffnen; Eingangsdatei bis zum Entwurf testen.',
      help: 'Hilfe zu den Lager- und Lieferabläufen',
      errors: {
        negative: 'Die Menge darf nicht negativ sein.',
        changedRetry:
          'Der Wiederholungsauftrag enthält andere Angaben. Prüfen und als neue Aktion speichern.',
        inventoryFrozen: 'Der Artikel ist für eine laufende Inventur gesperrt.',
        unresolvedStock:
          'Artikelbestand und Chargensumme stimmen nicht überein. Zuerst ausdrücklich klären.',
        locationNeedsWarehouse: 'Der Lagerplatz benötigt ein Lager.',
        unknownLocation: 'Lager oder Lagerplatz ist nicht aktiv oder unbekannt.',
        locationInsufficient: 'Am ausgewählten Lagerplatz reicht der verfügbare Bestand nicht aus.',
        selectStockLocation:
          'Bestand ist auf mehrere Plätze verteilt. Lagerplatz vor Warenausgang bestätigen.',
        quarantineIssue: 'Sperrbestand kann nicht als normale Lieferung entnommen werden.',
        quarantineInsufficient:
          'Die Menge überschreitet den verfügbaren Prüf- oder Freigabebestand.',
      },
      electronicAttachment: 'Validierte E-Rechnung erforderlich',
    },
  },
  en: {
    operations: {
      title: 'Set up warehouse and delivery workflows',
      description: 'Optional modules, reliable stock locations and validated invoice formats.',
      foundations: 'Existing foundations',
      baseline:
        'Products, packaging conversion, lots, stock journal, document chains and payment allocations are shared.',
      owner: 'Setup responsibility',
      activation: 'Activate in the feature catalogue',
      check: 'Verify activation',
      history: 'Deactivation retains history and blocks new work.',
      defaults: 'Add default warehouse and inspection bin',
      defaultsHint:
        'Adds warehouse master data and QUARANTINE bin if needed. Creates no stock movement and assigns no historical stock.',
      warehouses: 'Active warehouses',
      locations: 'Configured bins',
      unassigned: 'Products with unassigned stock',
      unresolved: 'Unresolved product/lot differences',
      returnSetup:
        'Customer service sets reasons; warehouse manager checks quarantine bin and release rights. Accounting uses credits and evidenced refunds. No external service required.',
      pickingSetup:
        'Warehouse manager checks product/package codes, factors, lots and delivery dates. Keyboard scanners and manual entry need no provider. Camera scanning depends on browser support.',
      inventorySetup:
        'Warehouse manager explicitly assigns unknown historical quantities and grants count/close rights. Products in a count are frozen for movements. No external service required.',
      tourSetup:
        'Dispatch assigns authorized drivers and checks saved addresses/coordinates. The operator configures road routing only when needed. GPS defaults to off.',
      containerSetup:
        'Administration marks actual reusable types and return conditions. Accounting optionally links a deposit product. Confirmed shipped packaging is required; PDF estimates never post balances.',
      einvoiceSetup:
        'Accounting completes company, recipient, tax and payment details. The operator installs the pinned independent validators. Import creates a draft for existing invoice review.',
      returnCheck:
        'Create a partial return of a test delivery, inspect quarantine and release after review.',
      pickingCheck: 'Scan a test carton, verify base quantity and open the delivery draft.',
      inventoryCheck:
        'Test blind counting with explicit zero, review a variance and close with permission.',
      tourCheck:
        'Assign a test delivery, post goods issue and confirm partial delivery as the assigned driver.',
      containerCheck:
        'Issue ten confirmed pallets and return six: balance four; check deposit separately.',
      einvoiceCheck:
        'Finalize a test invoice, validate externally and open reports; test import through draft creation.',
      help: 'Warehouse and delivery workflow help',
      errors: {
        negative: 'Quantity must not be negative.',
        changedRetry: 'The retry contains different data. Review and save as a new action.',
        inventoryFrozen: 'Product is frozen for an active inventory.',
        unresolvedStock: 'Product stock and lot totals differ. Resolve explicitly first.',
        locationNeedsWarehouse: 'A bin requires a warehouse.',
        unknownLocation: 'Warehouse or bin is inactive or unknown.',
        locationInsufficient: 'Available stock at the selected bin is insufficient.',
        selectStockLocation: 'Stock is spread across bins. Confirm a bin before goods issue.',
        quarantineIssue: 'Quarantined stock cannot be issued as a normal delivery.',
        quarantineInsufficient: 'Quantity exceeds available quarantine or released stock.',
      },
      electronicAttachment: 'Validated electronic invoice required',
    },
  },
  fr: {
    operations: {
      title: 'Configurer l’entrepôt et les livraisons',
      description: 'Modules facultatifs, emplacements fiables et factures validées.',
      foundations: 'Bases existantes',
      baseline:
        'Articles, conversions, lots, journal, chaîne documentaire et paiements sont communs.',
      owner: 'Responsable de la configuration',
      activation: 'Activer dans le catalogue',
      check: 'Vérifier l’activation',
      history: 'La désactivation conserve l’historique et bloque les nouveaux travaux.',
      defaults: 'Ajouter entrepôt et zone de contrôle',
      defaultsHint:
        'Ajoute au besoin l’entrepôt et QUARANTINE. Aucun mouvement ni attribution des stocks historiques.',
      warehouses: 'Entrepôts actifs',
      locations: 'Emplacements configurés',
      unassigned: 'Articles sans attribution du stock',
      unresolved: 'Écarts article/lot non résolus',
      returnSetup:
        'Le service clients configure les motifs, le magasin vérifie la quarantaine et les droits, la comptabilité traite avoirs et remboursements justifiés. Aucun service externe.',
      pickingSetup:
        'Le magasin vérifie codes, facteurs, lots et dates. Scanner clavier et saisie manuelle sans fournisseur. La caméra dépend du navigateur.',
      inventorySetup:
        'Le magasin attribue explicitement les anciens stocks inconnus et accorde les droits. Les articles comptés sont bloqués aux mouvements. Aucun service externe.',
      tourSetup:
        'L’expédition assigne les chauffeurs autorisés et vérifie adresses et coordonnées. Le routage est configuré si nécessaire. GPS désactivé par défaut.',
      containerSetup:
        'L’administration définit les emballages réutilisables et états de retour, la comptabilité lie éventuellement une consigne. Les emballages expédiés confirmés sont requis, sans écriture depuis les estimations PDF.',
      einvoiceSetup:
        'La comptabilité complète société, destinataire, taxes et paiements. L’exploitant installe les validateurs. L’import crée un brouillon à vérifier.',
      returnCheck: 'Testez un retour partiel, contrôlez la quarantaine puis libérez après examen.',
      pickingCheck: 'Scannez un carton test, vérifiez la quantité et ouvrez le brouillon.',
      inventoryCheck: 'Testez le comptage aveugle à zéro, l’écart et la clôture autorisée.',
      tourCheck:
        'Assignez une livraison test, comptabilisez la sortie et confirmez partiellement comme chauffeur assigné.',
      containerCheck:
        'Émettez dix palettes confirmées et reprenez six : solde quatre, consigne séparée.',
      einvoiceCheck:
        'Finalisez et validez une facture test, consultez le rapport et testez un import jusqu’au brouillon.',
      help: 'Aide entrepôt et livraison',
      errors: {
        negative: 'La quantité ne peut pas être négative.',
        changedRetry:
          'La répétition contient d’autres données. Vérifiez et créez une nouvelle action.',
        inventoryFrozen: 'Article bloqué par un inventaire actif.',
        unresolvedStock: 'Le stock et les lots diffèrent. Résolvez explicitement.',
        locationNeedsWarehouse: 'L’emplacement nécessite un entrepôt.',
        unknownLocation: 'Entrepôt ou emplacement inactif ou inconnu.',
        locationInsufficient: 'Stock insuffisant à l’emplacement sélectionné.',
        selectStockLocation: 'Stock réparti : confirmez l’emplacement avant la sortie.',
        quarantineIssue: 'Le stock en quarantaine ne peut être livré normalement.',
        quarantineInsufficient: 'Quantité supérieure au stock disponible.',
      },
      electronicAttachment: 'Facture électronique validée requise',
    },
  },
  es: {
    operations: {
      title: 'Configurar almacén y entregas',
      description: 'Módulos opcionales, ubicaciones fiables y facturas validadas.',
      foundations: 'Bases existentes',
      baseline: 'Artículos, conversiones, lotes, diario, documentos y pagos son compartidos.',
      owner: 'Responsable de configuración',
      activation: 'Activar en el catálogo',
      check: 'Verificar activación',
      history: 'La desactivación conserva el historial y bloquea nuevos procesos.',
      defaults: 'Añadir almacén y ubicación de revisión',
      defaultsHint:
        'Añade almacén y QUARANTINE si hace falta. Sin movimientos ni asignación de existencias históricas.',
      warehouses: 'Almacenes activos',
      locations: 'Ubicaciones configuradas',
      unassigned: 'Artículos con stock sin asignar',
      unresolved: 'Diferencias producto/lote sin resolver',
      returnSetup:
        'Atención al cliente define motivos, almacén verifica cuarentena y permisos, contabilidad gestiona abonos y reembolsos acreditados. Sin servicio externo.',
      pickingSetup:
        'Almacén verifica códigos, factores, lotes y fechas. Escáner teclado y entrada manual sin proveedor. Cámara depende del navegador.',
      inventorySetup:
        'Almacén asigna cantidades históricas desconocidas y concede permisos de conteo/cierre. Los artículos se bloquean durante el recuento. Sin servicio externo.',
      tourSetup:
        'Expedición asigna conductores autorizados y revisa direcciones/coordenadas. Operador configura rutas si hace falta. GPS apagado por defecto.',
      containerSetup:
        'Administración define envases retornables y estados, contabilidad vincula depósito opcional. Requiere envases enviados confirmados; estimaciones PDF no registran saldos.',
      einvoiceSetup:
        'Contabilidad completa empresa, receptor, impuestos y pagos. Operador instala validadores. Importación crea borrador para revisión existente.',
      returnCheck: 'Pruebe devolución parcial, cuarentena y liberación tras revisión.',
      pickingCheck: 'Escanee caja de prueba, verifique cantidad y abra borrador.',
      inventoryCheck: 'Pruebe recuento ciego con cero, diferencia y cierre autorizado.',
      tourCheck:
        'Asigne entrega de prueba, contabilice salida y confirme parcial como conductor asignado.',
      containerCheck:
        'Entregue diez palés confirmados y recupere seis: saldo cuatro; revise depósito aparte.',
      einvoiceCheck:
        'Finalize y valide factura de prueba, abra informe y pruebe importación hasta borrador.',
      help: 'Ayuda de almacén y entregas',
      errors: {
        negative: 'La cantidad no puede ser negativa.',
        changedRetry: 'El reintento contiene datos distintos. Revise y guarde nueva acción.',
        inventoryFrozen: 'Artículo bloqueado por inventario activo.',
        unresolvedStock: 'Existencias y lotes difieren. Resuelva explícitamente.',
        locationNeedsWarehouse: 'La ubicación requiere almacén.',
        unknownLocation: 'Almacén o ubicación inactivo o desconocido.',
        locationInsufficient: 'Stock insuficiente en ubicación seleccionada.',
        selectStockLocation: 'Stock repartido: confirme ubicación antes de salida.',
        quarantineIssue: 'Stock en cuarentena no puede entregarse normalmente.',
        quarantineInsufficient: 'Cantidad superior a stock disponible.',
      },
      electronicAttachment: 'Se requiere factura electrónica validada',
    },
  },
  tr: {
    operations: {
      title: 'Depo ve teslimat iş akışlarını kur',
      description: 'İsteğe bağlı modüller, güvenilir stok yerleri ve doğrulanmış faturalar.',
      foundations: 'Mevcut temeller',
      baseline:
        'Ürünler, ambalaj dönüşümü, partiler, stok günlüğü, belge zincirleri ve ödemeler ortak kullanılır.',
      owner: 'Kurulum sorumlusu',
      activation: 'Özellik kataloğunda etkinleştir',
      check: 'Etkinleştirmeyi doğrula',
      history: 'Devre dışı bırakma geçmişi korur ve yeni işlemleri engeller.',
      defaults: 'Varsayılan depo ve kontrol rafı ekle',
      defaultsHint:
        'Gerekirse depo ve QUARANTINE rafı ekler. Stok hareketi oluşturmaz veya eski stokları atamaz.',
      warehouses: 'Etkin depolar',
      locations: 'Yapılandırılan raflar',
      unassigned: 'Atanmamış stoğu olan ürünler',
      unresolved: 'Çözülmemiş ürün/parti farkları',
      returnSetup:
        'Müşteri hizmetleri nedenleri, depo karantina rafı ve izinleri, muhasebe alacak ve kanıtlı iadeleri düzenler. Harici hizmet gerekmez.',
      pickingSetup:
        'Depo ürün/ambalaj kodlarını, katsayıları, partileri ve tarihleri denetler. Klavye okuyucu ve manuel giriş sağlayıcısızdır. Kamera tarayıcıya bağlıdır.',
      inventorySetup:
        'Depo bilinmeyen eski miktarları açıkça atar ve sayım/kapanış yetkilerini verir. Sayılan ürünlerde hareketler kilitlidir. Harici hizmet gerekmez.',
      tourSetup:
        'Sevkiyat yetkili sürücüleri atar, adres ve koordinatları denetler. İşletmeci gerektiğinde yol yönlendirmesini kurar. GPS varsayılan kapalıdır.',
      containerSetup:
        'Yönetim gerçek iadeli türleri ve iade durumlarını belirler. Muhasebe isteğe bağlı depozito ürünü bağlar. Onaylı sevkiyat ambalajı gerekir; PDF tahminleri bakiyeyi kaydetmez.',
      einvoiceSetup:
        'Muhasebe şirket, alıcı, vergi ve ödeme bilgilerini tamamlar. İşletmeci sabit doğrulayıcıları kurar. İçe aktarma mevcut kontrol için taslak oluşturur.',
      returnCheck:
        'Test teslimatında kısmi iade, karantina ve inceleme sonrası serbest bırakmayı kontrol edin.',
      pickingCheck: 'Test kartonunu tarayın, temel miktarı ve irsaliye taslağını denetleyin.',
      inventoryCheck: 'Kör sayımda açık sıfır, fark ve yetkili kapanışı test edin.',
      tourCheck:
        'Test teslimatını atayın, stok çıkışını kaydedin ve atanmış sürücü olarak kısmi teslimatı onaylayın.',
      containerCheck:
        'Onaylı on palet çıkarıp altısını alın: bakiye dört; depozitoyu ayrı kontrol edin.',
      einvoiceCheck:
        'Test faturasını kesinleştirip haricen doğrulayın, raporu açın ve taslak içe aktarmayı test edin.',
      help: 'Depo ve teslimat iş akışı yardımı',
      errors: {
        negative: 'Miktar negatif olamaz.',
        changedRetry: 'Tekrar farklı veriler içeriyor. İnceleyip yeni işlem olarak kaydedin.',
        inventoryFrozen: 'Ürün etkin sayım için kilitli.',
        unresolvedStock: 'Ürün stoğu ve parti toplamı farklı. Önce açıkça çözün.',
        locationNeedsWarehouse: 'Raf bir depo gerektirir.',
        unknownLocation: 'Depo veya raf kapalı ya da bilinmiyor.',
        locationInsufficient: 'Seçilen rafta kullanılabilir stok yetersiz.',
        selectStockLocation: 'Stok raflara dağılmış. Çıkıştan önce rafı onaylayın.',
        quarantineIssue: 'Karantina stoğu normal teslimat için çıkarılamaz.',
        quarantineInsufficient: 'Miktar mevcut karantina veya serbest stoğu aşıyor.',
      },
      electronicAttachment: 'Doğrulanmış elektronik fatura gerekli',
    },
  },
} satisfies Record<SupportedLocale, unknown>;
